import {useHistory} from "react-router-dom";
import {useCallback, useEffect, useState} from "react";
import Api from "../../../helpers/api";
import {
    Badge,
    Button,
    LegacyCard,
    EmptyState,
    Form,
    Icon,
    ResourceItem,
    ResourceList,
    Spinner,
    LegacyStack,
    TextField,
    Text,
} from "@shopify/polaris";
import {SearchMajor} from "@shopify/polaris-icons";
import {Paginator} from "./Paginator";
import {AddressBlock} from "./AddressBlock";

const LocationsEmptyState = ({ locations }) => {
    if (locations.length) {
        return null;
    }

    return (<EmptyState heading="The customer has no additional locations"></EmptyState>)
}

export const LocationsCard = ({ customer, placeOrder }) => {
    if (! customer.id) {
        return null;
    }

    const history = useHistory();
    const [locations, setLocations] = useState([]);
    const [locationsMeta, setLocationsMeta] = useState({});
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const getLocations = useCallback((customerId, page = 1, query = null) => {
        let searchParam = query ?? search;

        if (searchParam && searchParam.length > 0 && searchParam.length < 3) {
            alert("You need to type at least 3 characters");
            return;
        }

        setLoading(true);
        Api
            .getLocations(customerId, page, searchParam)
            .then(({ data }) => {
                setLocations(data.data);
                setLocationsMeta(data.meta);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [locations, locationsMeta, loading, search])

    useEffect(() => {
        getLocations(customer.id, 1);
    }, [customer.id]);

    return (
        <LegacyCard
            title="Locations"
            sectioned
            actions={[
                { content: 'Add location', onAction: () => history.push(`/customers/${customer.id}/locations/create`) },
            ]}>

            <LegacyCard.Section>
                <Form
                    onSubmit={() => {
                        getLocations(customer.id, 1);
                    }}
                >
                    <TextField
                        autoComplete={'on'}
                        placeholder={"Search locations"}
                        value={search}
                        onChange={setSearch}
                        clearButton
                        onClearButtonClick={() => {
                            setSearch('');
                            getLocations(customer.id, 1, '');
                        }}
                        connectedRight={(
                            <Button

                                onClick={() => {
                                    getLocations(customer.id, 1)
                                }}
                                variant="primary">
                                <Icon source={SearchMajor} />
                            </Button>
                        )}
                    />
                </Form>
            </LegacyCard.Section>

            <Paginator
                collection={locations}
                meta={locationsMeta}
                callback={getLocations}
                customerId={customer?.id}
            />

            <LegacyCard.Section>
                { loading && (
                    <Spinner size={"small"} />
                )}
                { !loading && (
                    <ResourceList
                        resourceName={{singular: 'Location', plural: 'Locations'}}
                        items={locations}
                        emptyState={<LocationsEmptyState locations={locations} />}
                        renderItem={(location) => {
                            const shortcutActions = [
                                {
                                    content: 'Place Order',
                                    accessibilityLabel: `Place order for location ${location.id}`,
                                    onAction: () => { placeOrder(location.id); }
                                }
                            ]

                            const addresses = [
                                {location: location.billing, label: 'Billing Address'},
                                {location: location.shipping, label: 'Shipping Address'},
                            ].filter(item => item.location);

                            const badges = [
                                location.default && <Badge size='small'>Default</Badge>,
                                location.shopify_customer_id && <Badge size='small'>Shopify Customer</Badge>,
                            ].filter(Boolean);

                            return (
                                <ResourceItem onClick={ () => { history.push(`/locations/${location.id}`); }} shortcutActions={shortcutActions}>
                                    <LegacyStack vertical>
                                        <div><strong>{location.practice_name}</strong> {badges}</div>
                                        { location.phone && <div><Text  variant="bodyMd" as="span" fontWeight="semibold">Phone:</Text> {location.phone}</div> }

                                        <LegacyStack>
                                            {addresses.map((item, i) => (<AddressBlock {...item} key={`location-${i}`} />))}
                                        </LegacyStack>
                                        <div>
                                            <h3><Text  variant="bodyMd" as="span" fontWeight="semibold">Notes</Text></h3>
                                            {location.notes}
                                        </div>
                                    </LegacyStack>
                                </ResourceItem>
                            );
                        }}
                    />
                )}
            </LegacyCard.Section>

            <Paginator
                collection={locations}
                meta={locationsMeta}
                callback={getLocations}
                customerId={customer?.id}
            />
        </LegacyCard>
    );
}
